<script lang="ts">
	import { page } from '$app/state';
	import Chat from '#lib/Chat.svelte';
	import RightPanel from '#lib/RightPanel.svelte';
	import { setWindowContext, WindowContext } from '#src/contexts/window.context.svelte.ts';
	import { DEFAULT_FALLBACK_MODEL } from '@astlai/common/util/models';
	import { ChatContext, setChatContext } from '#src/contexts/chat.context.svelte.ts';
	import { DocumentContext, setDocumentContext } from '#src/contexts/documents.context.svelte.ts';
	import { LayoutContext, setLayoutContext } from '#src/contexts/layout.context.svelte.ts';
	import { ModalContext, setModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import { RightPanelContext, setRightPanelContext } from '#src/contexts/right-panel.context.svelte.ts';
	import { PbCoreApiService } from '#src/services/pb-core-api.service.svelte.ts';
	import { ChatMessage } from '#src/types/chat.ts';
	import { onMount } from 'svelte';

	interface Props {
		apiKey: string;
		baseUrl?: string;
		model?: string;
		initialMessage?: string;
		chatPlaceholder?: string;
	}

	let {
		apiKey,
		baseUrl = 'https://api.botbakery.app',
		model,
		initialMessage = `Hallo, wie kann ich dir helfen?`,
		chatPlaceholder
	}: Props = $props();

	// Initialize standalone contexts
	const chatCtx = new ChatContext(baseUrl, apiKey, {
		messages: [],
		model,
		sources: [],
		instance: undefined,
		title: 'Standalone Conversation',
		collectionId: 'pbc_3709231855',
		collectionName: 'conversation',
		id: null
	});
	chatCtx.standalone = true;
	setChatContext(chatCtx);

	$effect(() => {
		chatCtx.chatPlaceholder = chatPlaceholder;
	});

	setLayoutContext(new LayoutContext());
	const rightPanelCtx = setRightPanelContext(new RightPanelContext());
	const docCtx = setDocumentContext(new DocumentContext());
	setModalContext(new ModalContext());
	setWindowContext(new WindowContext());

	onMount(async () => {
		const pbService = new PbCoreApiService(baseUrl, apiKey);
		const instance = await pbService.getMyInstance();
		page.data.instance = instance;
		chatCtx.conversation.instance = instance.id;
		chatCtx.conversation.model = model || instance.default_model || DEFAULT_FALLBACK_MODEL;
		chatCtx.appendMessage(
			new ChatMessage('assistant', instance.welcome_message || initialMessage)
		);
		console.log('Standalone chat loaded!');
	});

	// Sync derived document data into document context
	$effect(() => {
		const kbSources = chatCtx.citedSources.filter((s) => s.web_id == null);
		docCtx.kbSources = kbSources;
		docCtx.documents = kbSources
			.map((s) => (s.doc_id != null ? chatCtx.documentMap.get(s.doc_id) : undefined))
			.filter((d): d is NonNullable<typeof d> => d !== undefined);
		docCtx.secondarySources = chatCtx.citedSources.filter((s) => s.web_id != null);
	});

	// Open the panel when a source is selected
	$effect(() => {
		if (docCtx.selected != null && !rightPanelCtx.isOpen) {
			rightPanelCtx.open('sources');
		}
	});
</script>

<div class="standalone-chat">
	<div class="chat-area">
		<Chat />
	</div>
	<RightPanel />
</div>

<style lang="scss">
	.standalone-chat {
		display: flex;
		flex-direction: row;
		height: 100%;
		width: 100%;
		overflow: hidden;
		background: var(--surface-1);
		position: relative;
	}

	.chat-area {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		overflow: hidden;
	}
</style>
